// SECTION-BY-SECTION PREDICTION -- built, INACTIVE (Kevin, 2026-10-18).
//
// "Everything in the firetube tab is collapsed except the top section and after if anything
// changes in that section then we can repredict for the other sections. After that the user
// would open the next section meaning the section before that cannot be edited by a prediction
// only the collapsed ones can." "The rep will open a section and review the questions; if the
// rep goes to the next section then that means the first section is correct."
//
// So, on every tab after the first:
//   - sections start collapsed, except the first, which is OPEN (under review);
//   - "Next section" (or clicking a collapsed header) CONFIRMS every open section and opens the
//     next one; a confirmed section stays visible and is never predicted over again;
//   - a change in an open or confirmed section re-predicts the COLLAPSED sections only, from
//     evidence that agrees with what is confirmed (components/SectionPrediction).
//
// OFF, and not switchable from outside: no URL parameter, no storage. It stays in the code
// until Kevin says "we will use that instead" -- then SECTION_MODE is set to true here.

import { useSyncExternalStore } from "react";

export const SECTION_MODE = false;
export const sectionModeOn = (): boolean => SECTION_MODE;

type State = { opened: ReadonlySet<string>; confirmed: ReadonlySet<string>; seq: number };
let state: State = { opened: new Set(), confirmed: new Set(), seq: 0 };
const listeners = new Set<() => void>();
const emit = () => { state = { ...state, seq: state.seq + 1 }; for (const l of listeners) l(); };
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
export const useSections = () => useSyncExternalStore(subscribe, () => state);
export const sectionsNow = () => state;

/** The section under review, or the first one when a tab is shown for the first time. */
export function openSection(groupId: string): void {
  if (state.opened.has(groupId) || state.confirmed.has(groupId)) return;
  const confirmed = new Set(state.confirmed);
  for (const g of state.opened) confirmed.add(g);
  state = { ...state, opened: new Set([groupId]), confirmed };
  emit();
}

/** A tab shown with none of its sections open or confirmed opens its first. */
export function ensureFirstOpen(firstGroupId: string | undefined, groupIds: string[]): void {
  if (!firstGroupId) return;
  if (groupIds.some((g) => state.opened.has(g) || state.confirmed.has(g))) return;
  state = { ...state, opened: new Set([...state.opened, firstGroupId]) };
  emit();
}

export const isOpen = (groupId: string) => state.opened.has(groupId);
export const isConfirmed = (groupId: string) => state.confirmed.has(groupId);
/** Collapsed: neither open nor confirmed -- the only sections a prediction may still change. */
export const isCollapsedSection = (groupId: string) => !state.opened.has(groupId) && !state.confirmed.has(groupId);

/** For tests and the harness: start over. */
export function resetSections(): void {
  state = { opened: new Set(), confirmed: new Set(), seq: 0 };
  emit();
}
